/**
 * Validation functions for Add Client Form
 */

/**
 * Validates individual form fields
 * @param {string} name - Field name
 * @param {any} value - Field value
 * @returns {string} Error message or empty string if valid
 */
export const validateField = (name, value) => {
  switch (name) {
    case "client_name":
      if (!value || !value.toString().trim()) return "Client name is required";
      if (value.toString().length < 2)
        return "Client name must be at least 2 characters";
      return "";

    case "opening_cd_balance":
      if (value === "" || value === null || value === undefined)
        return "Opening CD balance is required";
      if (isNaN(value) || Number(value) < 0)
        return "Opening CD balance must be a positive number";
      return "";

    case "opening_cd_balance_premium":
      if (value === "" || value === null || value === undefined)
        return "Opening Premium CD balance is required";
      if (isNaN(value) || Number(value) < 0)
        return "Opening Premium CD balance must be a positive number";
      return "";

    case "maximum_voucher_value":
      if (value === "" || value === null || value === undefined)
        return "Maximum voucher amount is required (in INR)";
      if (isNaN(value) || Number(value) < 1)
        return "Maximum voucher amount must be at least 1";
      if (!Number.isInteger(Number(value)))
        return "Maximum voucher amount must be a whole number";
      if (Number(value) > 999999)
        return "Maximum voucher amount cannot exceed 999999";
      return "";

    case "claim_intimation_timeline":
      if (value === "" || value === null || value === undefined)
        return "Maximum time for claim intimation is required (in Hours)";
      if (isNaN(value) || Number(value) < 1)
        return "Maximum time must be at least 1 hour";
      if (!Number.isInteger(Number(value)))
        return "Maximum time must be a whole number";
      if (Number(value) > 168) return "Maximum time cannot exceed a week";
      return "";

    case "voucher_validity_timeline":
      if (value === "" || value === null || value === undefined)
        return "Maximum voucher validity is required (in days)";
      if (isNaN(value) || Number(value) < 1)
        return "Maximum voucher validity must be at least 1";
      if (!Number.isInteger(Number(value)))
        return "Maximum voucher validity must be a whole number";
      if (Number(value) > 365)
        return "Maximum voucher validity cannot exceed 365";
      return "";

    default:
      return "";
  }
};

/**
 * Validates the entire form
 * @param {Object} formData - Form data object
 * @returns {Object} Object with errors and isValid flag
 */
export const validateForm = (formData) => {
  const errors = {};

  Object.keys(formData).forEach((key) => {
    const error = validateField(key, formData[key]);
    if (error) errors[key] = error;
  });

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
  };
};

/**
 * Input validation and filtering for form fields
 * @param {string} name - Field name
 * @param {string} value - Input value
 * @returns {Object} Object with isValid flag and error message
 */
export const validateInput = (name, value) => {
  // Filter out decimal and negative values for the three new fields
  if (
    name === "maximum_voucher_value" ||
    name === "claim_intimation_timeline" ||
    name === "voucher_validity_timeline"
  ) {
    // Only allow positive integers
    if (value !== "" && (!/^\d+$/.test(value) || Number(value) < 1)) {
      return {
        isValid: false,
        error: "Only positive whole numbers are allowed",
      };
    }
  }

  // Length validation for numeric fields
  if (name === "opening_cd_balance" && value.length > 10) {
    return {
      isValid: false,
      error: "Amount cannot exceed 10 digits",
    };
  }

  if (name === "maximum_voucher_value" && value.length > 6) {
    return {
      isValid: false,
      error: "Amount cannot exceed 6 digits",
    };
  }

  if (
    (name === "claim_intimation_timeline" ||
      name === "voucher_validity_timeline") &&
    value.length > 3
  ) {
    return {
      isValid: false,
      error: "Days cannot exceed 3 digits",
    };
  }

  return {
    isValid: true,
    error: "",
  };
};

/**
 * Checks if form data has changed from original values
 * @param {Object} currentData - Current form data
 * @param {Object} originalData - Original form data
 * @returns {boolean} True if form has changed
 */
export const hasFormChanged = (currentData, originalData) => {
  if (!originalData) return false;

  return Object.keys(currentData).some((key) => {
    const currentValue = currentData[key];
    const originalValue = originalData[key];

    // Handle numeric fields
    if (typeof currentValue === "number" || typeof originalValue === "number") {
      return Number(currentValue) !== Number(originalValue);
    }

    // Handle boolean fields
    if (
      typeof currentValue === "boolean" ||
      typeof originalValue === "boolean"
    ) {
      return Boolean(currentValue) !== Boolean(originalValue);
    }

    // Handle string fields
    return String(currentValue).trim() !== String(originalValue).trim();
  });
};
